import useFeatureFlags, { FeatureFlag } from '@/composables/feature-flags';
import { useIsChangingActiveSet } from '@/modules/offline-assessment/api';
import { ConnectionStatus } from '@@/packages/shared/types/models/connection';
import { useSupplierInfo } from '../api';
import type { SupplierInfoResponse } from '../api/supplier';
import { useTargetOrgType } from '../targetOrgType';

type ConnectionFacts = {
  orgID: string;
  isSupplier: boolean;
  supplierInfo: SupplierInfoResponse | undefined;
  isChangingSet: boolean;
};

// What the profile of the org on the other side of a connection shows. Each flag
// says whether the connection allows a feature; the user's permissions are
// checked where they act. Route guards read it too, so a tab that is off cannot
// be opened by its address.
//
// A client's overrides, remediations, discussions and AI review and summary need
// a full connection that reads the supplier's own answers. The backend refuses and
// withholds them on any other, so they stay off until this supplier's own info is
// in, rather than showing the previous supplier's held over while it loads. A
// supplier looking at its client sees the client's side of them all.
export function connectionCapabilities({
  orgID,
  isSupplier,
  supplierInfo,
  isChangingSet,
}: ConnectionFacts) {
  const isConnectionKnown = supplierInfo?.orgInfo?.id === orgID;
  const isFull = isConnectionKnown && supplierInfo?.connectionStatus === ConnectionStatus.Full;
  const isBasicConnection = isSupplier && isConnectionKnown && !isFull;
  const isClientSetActive =
    isSupplier && isConnectionKnown && supplierInfo?.answerSet?.kind === 'client';

  // Off while a start or switch of set is in flight too, since the cached info can
  // still name the supplier's set after the connection has moved to the client's.
  const isClientSideOn = !isSupplier || (isFull && !isClientSetActive && !isChangingSet);

  return {
    isConnectionKnown,
    isBasicConnection,
    isClientSetActive,
    connectionAllowsDiscussions: isClientSideOn,
    connectionAllowsRemediations: isClientSideOn,
    connectionAllowsOverrides: isClientSideOn,
    // The AI review and summary are of the supplier, for its clients only.
    isAIOn: isSupplier && isClientSideOn,
    // A client set gives a basic connection activity of its own to show. Off while a
    // switch is in flight, since the cached info can still name the client set after
    // the connection has left it.
    connectionAllowsActivity: !isBasicConnection || (isClientSetActive && !isChangingSet),
    connectionAllowsCriticalSuppliers: !isSupplier || isFull,
    // A basic connection lists critical suppliers it can't open, except while its
    // client set is active, when they are not the client's to see at all.
    connectionShowsCriticalSuppliers: !(isBasicConnection && isClientSetActive),
  };
}

export function useConnectionCapabilities(
  orgID: MaybeRefOrGetter<string>,
  options?: { type?: MaybeRefOrGetter<'client' | 'supplier'> },
) {
  const { ff } = useFeatureFlags();
  const targetOrgType = useTargetOrgType(options?.type);
  const isSupplier = computed(() => targetOrgType.value === 'supplier');
  const { supplierInfo } = useSupplierInfo(orgID, {
    enabled: () => isSupplier.value,
  });
  const isChangingSet = useIsChangingActiveSet(orgID);

  const capabilities = computed(() =>
    connectionCapabilities({
      orgID: toValue(orgID),
      isSupplier: isSupplier.value,
      supplierInfo: supplierInfo.value,
      isChangingSet: isChangingSet.value,
    }),
  );
  return {
    isConnectionKnown: computed(() => capabilities.value.isConnectionKnown),
    isBasicConnection: computed(() => capabilities.value.isBasicConnection),
    isClientSetActive: computed(() => capabilities.value.isClientSetActive),
    connectionAllowsDiscussions: computed(() => capabilities.value.connectionAllowsDiscussions),
    connectionAllowsRemediations: computed(() => capabilities.value.connectionAllowsRemediations),
    connectionAllowsOverrides: computed(() => capabilities.value.connectionAllowsOverrides),
    connectionAllowsAIReview: computed(
      () => ff(FeatureFlag.AIAssessmentReview) && capabilities.value.isAIOn,
    ),
    connectionAllowsAISummary: computed(() => capabilities.value.isAIOn),
    connectionAllowsActivity: computed(() => capabilities.value.connectionAllowsActivity),
    connectionAllowsCriticalSuppliers: computed(
      () => capabilities.value.connectionAllowsCriticalSuppliers,
    ),
    connectionShowsCriticalSuppliers: computed(
      () => capabilities.value.connectionShowsCriticalSuppliers,
    ),
  };
}
